import { Component, EventEmitter, Input, Output, OnDestroy } from '@angular/core';
import { Attachment, UploadParameters, UploadParametersRequest } from '../../models/attachment';
import { AttachmentService } from '../../../services/attachment.service';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements OnDestroy {
  @Input() acceptedTypes: string = 'image/*';
  @Input() maxSize: number = 5 * 1024 * 1024; // 5MB default
  @Input() placeholder: string = 'Drag & drop files here or click to select';
  @Input() disabled: boolean = false;
  @Input() currentFile?: { id?: string; fileName?: string; extension?: string; url?: string; };
  /**
   * Whether picking a file should immediately push it to S3. True for the attachment cases this
   * was written for (partner logos). False when the caller wants the File itself — an onboarding
   * import posts it to our own API as multipart and never wants an attachment record.
   */
  @Input() autoUpload: boolean = true;

  @Output() fileSelected = new EventEmitter<File>();
  @Output() uploadStarted = new EventEmitter<{ file: File; uploadParams: UploadParameters }>();
  @Output() uploadCompleted = new EventEmitter<{ id: number; fileName: string }>();
  @Output() uploadError = new EventEmitter<string>();
  @Output() fileRemoved = new EventEmitter<void>();
  @Output() uploadProgress = new EventEmitter<number>();

  isDragOver = false;
  isUploading = false;
  error: string | null = null;
  uploadProgressValue = 0;
  selectedFile: File | null = null;
  filePreviewUrl: string | null = null;

  constructor(private attachmentService: AttachmentService) {
    console.log('🔥 FileUploadComponent constructor called');
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    if (this.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    if (this.disabled) return;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  private handleFile(file: File): void {
    this.error = null;

    // Validate file type
    if (this.acceptedTypes && !this.isFileTypeAccepted(file)) {
      this.error = 'File type not accepted';
      return;
    }

    // Validate file size
    if (file.size > this.maxSize) {
      this.error = `File size exceeds maximum allowed size of ${this.formatFileSize(this.maxSize)}`;
      return;
    }

    // Store the selected file and create preview URL
    this.selectedFile = file;
    this.createFilePreview(file);
    
    console.log('🔥 File selected, emitting fileSelected event');
    this.fileSelected.emit(file);
    if (this.autoUpload) {
      this.startUpload(file);
    }
  }

  private createFilePreview(file: File): void {
    // Clean up previous preview URL
    if (this.filePreviewUrl) {
      URL.revokeObjectURL(this.filePreviewUrl);
    }

    // Create preview URL for images
    if (file.type.startsWith('image/')) {
      this.filePreviewUrl = URL.createObjectURL(file);
    } else {
      this.filePreviewUrl = null;
    }
  }

  private startUpload(file: File): void {
    this.isUploading = true;
    this.uploadProgressValue = 0;
    this.error = null;

    this.attachmentService.uploadFileWithProgress(file).subscribe({
      next: (result) => {
        console.log('🔥 Upload progress result:', result);
        this.uploadProgressValue = result.progress;
        this.uploadProgress.emit(result.progress);
        
        console.log('🔥 Progress check - progress:', result.progress, 'attachmentId:', result.attachmentId);
        
        if (result.progress === 100 && result.attachmentId) {
          console.log('📤 Upload completed, attachment ID:', result.attachmentId);
          console.log('📤 Selected file name:', this.selectedFile?.name);
          this.isUploading = false;
          const uploadData = {
            id: result.attachmentId,
            fileName: this.selectedFile?.name || 'uploaded-file'
          };
          console.log('📤 Emitting uploadCompleted with data:', uploadData);
          console.log('📤 About to emit uploadCompleted event...');
          this.uploadCompleted.emit(uploadData);
          console.log('📤 uploadCompleted event emitted!');
        } else {
          console.log('🔥 Upload not complete yet - progress:', result.progress, 'attachmentId:', result.attachmentId);
        }
      },
      error: (error) => {
        console.error('Upload error:', error);
        this.isUploading = false;
        this.error = 'Upload failed. Please try again.';
        console.log('🔥 Upload error, emitting uploadError event');
        this.uploadError.emit(this.error);
      },
      complete: () => {
        console.log('Upload observable completed');
        // Ensure upload state is reset when observable completes
        if (this.isUploading) {
          console.log('Resetting upload state from complete handler');
          this.isUploading = false;
        }
      }
    });
  }

  private isFileTypeAccepted(file: File): boolean {
    if (!this.acceptedTypes) return true;
    
    const acceptedTypes = this.acceptedTypes.split(',').map(type => type.trim());
    return acceptedTypes.some(type => {
      // An extension pattern (".csv"). Browsers disagree about the MIME type of a CSV — text/csv,
      // application/vnd.ms-excel, or nothing at all — so the name is the only reliable test.
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.slice(0, -1));
      }
      return file.type === type;
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onRemoveFile(): void {
    // Clean up preview URL
    if (this.filePreviewUrl) {
      URL.revokeObjectURL(this.filePreviewUrl);
      this.filePreviewUrl = null;
    }
    
    // Clear selected file
    this.selectedFile = null;
    
    console.log('🔥 onRemoveFile called, emitting fileRemoved event');
    this.fileRemoved.emit();
  }

  getFileName(): string {
    if (this.selectedFile) {
      return this.selectedFile.name;
    }
    return this.currentFile?.fileName || '';
  }

  getFileSize(): string {
    if (this.selectedFile) {
      return this.formatFileSize(this.selectedFile.size);
    }
    // For existing files, we don't have size information
    return '';
  }

  getFilePreviewUrl(): string | null {
    // Return local file preview if available
    if (this.filePreviewUrl) {
      console.log('Using local file preview URL:', this.filePreviewUrl);
      return this.filePreviewUrl;
    }
    
    // Return existing attachment URL if available
    if (this.currentFile?.url) {
      console.log('Using existing attachment URL:', this.currentFile.url);
      return this.currentFile.url;
    }
    
    console.log('No preview URL available');
    return null;
  }

  hasSelectedFile(): boolean {
    return this.selectedFile !== null;
  }

  ngOnDestroy(): void {
    // Clean up preview URL to prevent memory leaks
    if (this.filePreviewUrl) {
      URL.revokeObjectURL(this.filePreviewUrl);
    }
  }
}
